import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import { FEATURED_PATH, isGuestAuthPromptPath, isPublicRoutePath } from "@/constants/appChrome";
import { CONSOLE_ROUTE_PAGES, consolePageSlug } from "@/views/live/consolePages";
import { applyRouteDocumentTitle } from "@/i18n/routeTitle";
import { useGlobalStore } from "@/store/global";
import { useLoginDialogStore } from "@/store/loginDialog";
import { usePowerStore } from "@/store/power";
import appUpdate from "@/service/appUpdate";
import { isEnterpriseLevel } from "@/constants/userLevel";
import { capturePendingInviteCode } from "@/utils/inviteCapture";

// 静态路由文件
import other from "./other";

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    component: () => import("@/views/RootEntry.vue"),
    children: [
      {
        path: "",
        component: () => import("@/views/main/Main.vue"),
        children: [
          {
            path: "",
            redirect: FEATURED_PATH,
          },
          {
            path: "inspiration",
            meta: { title: "灵感中心" },
            component: () => import("@/views/inspiration/index.vue"),
          },
          {
            path: "featured",
            meta: { title: "精选" },
            component: () => import("@/views/featured/index.vue"),
          },
          {
            /** 推荐：未登录停在本页弹登录框；已登录直接进视频详情 */
            path: "recommend",
            meta: { title: "推荐" },
            component: () => import("@/views/recommend/index.vue"),
          },
          {
            /**
             * 视频详情页，对齐原型 `/app/watch/:id`。
             * 游客可访问（由 `isPublicRoutePath` 前缀放行），与精选页口径一致。
             */
            path: "watch/:id",
            meta: { title: "视频详情" },
            component: () => import("@/views/watch/index.vue"),
          },
          {
            path: "drama",
            meta: { title: "短剧" },
            component: () => import("@/views/drama/index.vue"),
          },
          {
            /** 短剧播放页。游客可看免费集，付费集在页内登录后再解锁 */
            path: "drama/:id",
            meta: { title: "短剧播放" },
            component: () => import("@/views/drama/play.vue"),
          },
          {
            /** AI 直播。未登录停在模块内并弹出登录框，资格页和控制台由壳决定 */
            path: "ai-live",
            meta: { title: "AI 直播" },
            component: () => import("@/views/live/LiveShell.vue"),
            children: [
              {
                path: "operators",
                meta: { title: "子账号维护" },
                component: () => import("@/views/live/operators.vue"),
              },
              {
                /** 功能页挂在壳下面。只有资格通过时壳才会渲染这里 */
                path: "",
                component: () => import("@/views/live/console.vue"),
                children: CONSOLE_ROUTE_PAGES.map((page) => ({
                  path: consolePageSlug(page),
                  name: page,
                  meta: { title: "AI 直播" },
                  component: () => import("@/views/live/ConsoleFrame.vue"),
                })),
              },
            ],
          },
          {
            /** 顶栏搜索结果页（与下拉共用同一个查询参数 ?q=&type=） */
            path: "search",
            meta: { title: "搜索" },
            component: () => import("@/views/search/index.vue"),
          },
          {
            path: "following/watch/:id",
            meta: { title: "关注" },
            component: () => import("@/views/watch/WatchPage.vue"),
          },
          {
            path: "following",
            meta: { title: "关注" },
            component: () => import("@/views/following/index.vue"),
          },
          {
            path: "me",
            meta: { title: "我的" },
            component: () => import("@/views/me/index.vue"),
          },
          {
            /**
             * 我的 → 梦想豆收支明细（独立页）。
             *
             * 刻意**不进** `PUBLIC_ROUTE_PATHS`：明细接口都需要登录态，
             * 游客访问会被守卫拦到精选页并弹登录框（与原来的「我的订单」口径一致）。
             */
            path: "me/bean-records",
            meta: { title: "梦想豆收支明细" },
            component: () => import("@/views/me/beanRecords.vue"),
          },
          {
            /**
             * 我的 → 编辑资料（独立页）。
             *
             * 不进 `PUBLIC_ROUTE_PATHS`：资料接口需要登录态，
             * 游客访问会被守卫拦到精选页并弹登录框。
             */
            path: "me/profile",
            meta: { title: "编辑资料" },
            component: () => import("@/views/me/profile.vue"),
          },
          {
            /**
             * 我的 → 我的分佣（独立页）。
             *
             * 对应原型 `docs/prototype/会员分佣-原型-真实版-20261012.html` 的
             * `screen-commission`。不进 `PUBLIC_ROUTE_PATHS`：分佣数据必须登录后可见。
             */
            path: "me/commission",
            meta: { title: "我的分佣" },
            component: () => import("@/views/me/commission.vue"),
          },
          {
            /**
             * 我的 → 我的会员（独立页）。
             *
             * 对应原型 `screen-member`：档位卡 + 六项统计 + 生效权益清单。
             * 不进 `PUBLIC_ROUTE_PATHS`：会员数据必须登录后可见。
             */
            path: "me/membership",
            meta: { title: "我的会员" },
            component: () => import("@/views/me/membership.vue"),
          },
          {
            /** 我的会员 → 选择会员档位（原型 `screen-tiers`）；需登录 */
            path: "me/tiers",
            meta: { title: "选择会员" },
            component: () => import("@/views/me/tiers.vue"),
          },
          {
            /**
             * 线下付款 · 待确认页（原型 `screen-payment`）。
             *
             * 下单后带 `?orderNo=` 跳进来：展示收款信息 + 倒计时 + 「我已付款」+ 状态时间线。
             * `pay.mode=offline` 时会员 / 充豆 / 继续支付都落到这里。
             * 不进 `PUBLIC_ROUTE_PATHS`：收款账号只应给登录用户看。
             */
            path: "me/payment",
            meta: { title: "线下付款" },
            component: () => import("@/views/me/payment.vue"),
          },
          {
            /**
             * 我的 → 子账户与共享池（原型 `screen-seats`）；仅 50 / 100 档可用，需登录
             */
            path: "me/seats",
            meta: { title: "子账户与共享池" },
            component: () => import("@/views/me/seats.vue"),
          },
          {
            path: "home",
            meta: { title: "首页" },
            component: () => import("@/views/home/Index.vue"),
          },
          {
            path: "resource",
            meta: { title: "资产库" },
            component: () => import("@/views/resource/index.vue"),
          },
          {
            path: "my-works",
            meta: { title: "我的作品" },
            component: () => import("@/views/myWorks/index.vue"),
          },
          {
            path: "tools",
            meta: { title: "小工具" },
            component: () => import("@/views/tools/index.vue"),
          },
          {
            path: "tools/one-click-hd",
            meta: { title: "一键高清" },
            component: () => import("@/views/tools/oneClickHd/index.vue"),
          },
          {
            path: "breakdown",
            meta: { title: "爆款拆解" },
            component: () => import("@/views/breakdown/index.vue"),
          },
          {
            path: "assets",
            redirect: "/resource",
          },
          {
            path: "segmentVideo",
            meta: { title: "视频切片" },
            component: () => import("@/views/segmentVideo/index.vue"),
          },
          {
            /** 创作入口页：先展示「自主创作」「元素工坊」两个磁贴 */
            path: "create",
            meta: { title: "创作" },
            component: () => import("@/views/create/CreateHome.vue"),
          },
          {
            /** 自主创作（原 /ai-creation，2026-11-03 迁到 /create 下） */
            path: "create/ai-creation",
            meta: { title: "自主创作" },
            component: () => import("@/views/aiCreation/index.vue"),
          },
          {
            /** 元素工坊/爆款复刻（原 /replication，2026-11-03 迁到 /create 下） */
            path: "create/replication",
            meta: { title: "爆款复刻" },
            component: () => import("@/views/create/index.vue"),
          },
          {
            /** 旧地址兼容重定向：书签 / 历史记录里的老链接 */
            path: "ai-creation",
            redirect: "/create/ai-creation",
          },
          {
            path: "replication",
            redirect: "/create/replication",
          },
          {
            path: "batch-replication",
            meta: { title: "批量复刻" },
            component: () => import("@/views/batchReplication/index.vue"),
          },
          {
            path: "digital-human",
            redirect: "/resource",
          },
          {
            /** 切片裂变：步骤1 素材上传与拓影解析 */
            path: "slice-fission",
            meta: { title: "切片裂变" },
            component: () => import("@/views/sliceFission/index.vue"),
          },
          {
            path: "pricing",
            meta: { title: "定价充值" },
            component: () => import("@/views/pricing/index.vue"),
          },
          {
            path: "profile",
            meta: { title: "个人中心" },
            component: () => import("@/views/profile/index.vue"),
          },
          {
            path: "tasks",
            meta: { title: "任务管理" },
            component: () => import("@/views/taskManagement/index.vue"),
          },
          {
            path: "pay-test",
            meta: { title: "支付测试" },
            component: () => import("@/views/payTest/index.vue"),
          },
          {
            path: "feedback",
            meta: { title: "用户反馈" },
            component: () => import("@/views/feedback/index.vue"),
          },
          {
            path: "user-management",
            meta: { title: "用户管理" },
            component: () => import("@/views/userManagement/index.vue"),
          },
          {
            path: "admin/logs",
            meta: { title: "系统管理", requiresAdmin: true },
            component: () => import("@/views/logAdmin/index.vue"),
          },
          {
            path: "admin/tenants",
            meta: { title: "企业管理", requiresAdmin: true },
            component: () => import("@/views/adminTenant/index.vue"),
          },
          {
            path: "admin/power",
            meta: { title: "梦想豆管理", requiresAdmin: true },
            component: () => import("@/views/adminPower/index.vue"),
          },
          {
            /**
             * 订单入口（所有人可见）：
             * 管理员 → 财务统计视图；普通用户 → 自己的订单；游客由守卫弹登录框。
             * 旧管理端地址重定向保留兼容。
             */
            path: "orders",
            meta: { title: "订单" },
            component: () => import("@/views/orders/index.vue"),
          },
          {
            path: "admin/finance",
            redirect: "/orders",
          },
          {
            /**
             * 退款 · 撤佣 + 回收权益（原型 `screen-refund`）。
             *
             * 从财务统计的充值记录带 `?orderNo=` 跳入，或手动输入订单号。
             * 路由守卫已校验 role=admin（{@link requiresAdmin}）。
             */
            path: "admin/refund",
            meta: { title: "退款撤佣", requiresAdmin: true },
            component: () => import("@/views/adminRefund/index.vue"),
          },
          {
            /**
             * 分佣管理（管理员）：计佣规则 / 佣金明细 / 结算批次 / 对账体检 四页 + 手动补跑月结。
             * 路由守卫已校验 role=admin（{@link requiresAdmin}）。
             */
            path: "admin/commission",
            meta: { title: "分佣管理", requiresAdmin: true },
            component: () => import("@/views/adminCommission/index.vue"),
          },
          {
            /**
             * 会员权益配置（管理员，原型 `screen-benefits`）。
             *
             * 3 档平铺：每档独立维护权益清单（增 / 改 / 删 + 从权益库添加）。
             * 路由守卫已校验 role=admin（{@link requiresAdmin}）。
             */
            path: "admin/benefits",
            meta: { title: "权益配置", requiresAdmin: true },
            component: () => import("@/views/adminBenefits/index.vue"),
          },
          {
            /**
             * 精选页「官方账号内置视频」管理（管理员）。
             *
             * 上传本地 MP4 作为官方账号的内置视频，游客在精选页可直接看到。
             * 路由守卫已校验 role=admin（{@link requiresAdmin}）。
             */
            path: "admin/official-videos",
            meta: { title: "官方内置视频", requiresAdmin: true },
            component: () => import("@/views/adminOfficialVideo/index.vue"),
          },
          {
            path: "admin/training",
            meta: { title: "培训管理", requiresAdmin: true },
            component: () => import("@/views/training/manage/index.vue"),
          },
          {
            path: "admin/training/sessions/:id/registrations",
            meta: { title: "报名详情", requiresAdmin: true },
            component: () => import("@/views/training/registrations/index.vue"),
          },
          {
            path: "tenant-member",
            meta: { title: "团队管理", requiresTenantAdmin: true },
            component: () => import("@/views/tenantMember/layout.vue"),
            redirect: "/tenant-member/list",
            children: [
              {
                path: "list",
                meta: { title: "成员列表", requiresTenantAdmin: true },
                component: () => import("@/views/tenantMember/MemberList.vue"),
              },
              {
                path: "records",
                meta: { title: "企业账户明细", requiresTenantAdmin: true },
                component: () => import("@/views/tenantMember/AccountRecords.vue"),
              },
            ],
          },
          ...other,
        ],
      },
    ],
  },
  {
    path: "/training/enroll",
    component: () => import("@/views/training/enroll/index.vue"),
  },
  {
    path: "/login",
    redirect: FEATURED_PATH,
  },
  {
    name: "404",
    path: "/:catchAll(.*)",
    component: () => import("@/views/other/404.vue"),
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

router.beforeEach(async (to, from, next) => {
  // 0.-2> 动态读取 config.json 配置 -------------------------------------------------------------------
  // ⚠️ 必须排在所有网络请求之前：`service/common.getEnvVar()` 的取值来源就是这个数组，
  //    config 为空时 `VITE_APP_BASE_URL` 返回空串 ⇒ 请求会拼成裸路径（如 `/commission/scan`），
  //    绕过 vite 的 `/AiVideo` 代理 / 线上 nginx 转发 ⇒ 404 ⇒ 首页弹「服务器开小差了」。
  //    历史上这段排在「扫码落地捕获邀请码」之后，导致扫码进来的第一个埋点请求必挂。
  const globalStore = useGlobalStore();
  if (globalStore.config.length <= 0) {
    const res = await fetch(utils.common.getFullPath(router, "/config.json"));
    globalStore.config = await res.json();
  }

  // 0.-1> 扫码落地捕获邀请码 -----------------------------------------------------------------------
  // 推荐官海报二维码指向 `/?invite=XXXXXX`。游客访问会被下面的守卫重定向到精选页，
  // query 会丢，所以必须在重定向**之前**把邀请码先落到 sessionStorage；
  // 登录/注册弹窗打开时（LoginDialog）读取并回填到注册表单的邀请码字段。
  // ⚠️ 两条来源都要读：`/` → `/featured` 是**路由级 redirect**，守卫拿到的 `to` 是重定向后的
  //    目标（query 已丢），原 query 只能从 `to.redirectedFrom` 里取。
  const pendingInviteCaptured =
    capturePendingInviteCode(to.query.invite) ||
    capturePendingInviteCode(to.redirectedFrom?.query?.invite);

  // 0.0> 路由切换时检测新版本，hash 不一致则刷新页面 -----------------------------------------------
  if (import.meta.env.PROD && from.name !== undefined) {
    const reloaded = await appUpdate.checkOnRouteChange(to.fullPath);
    if (reloaded) return;
  }

  // 0.1 页面顶部进度条：开始显示进度 ------------------------------------------------------------------
  nprogressStart();

  // 1.0> config.json 已在守卫最前面加载（见 0.-2>，必须先于任何请求）

  // 2.0> 浏览器标题走三语文案，避免写回 meta 里的中文
  applyRouteDocumentTitle(to.path, to.meta.title);

  // 3.0> 登录校验：公开页可逛，登录改为弹窗，落地页/登录页不再作为独立入口
  const token = common.getToken();
  const signKey = common.getSignKey();
  const isGuest = token === null || signKey === null;
  const loginSource = to.path === "/login" ? to : to.redirectedFrom?.path === "/login" ? to.redirectedFrom : null;
  const isPublicPath = isPublicRoutePath(to.path) || to.name === "404";

  if (loginSource) {
    if (isGuest) {
      const redirectQuery = loginSource.query.redirect;
      const redirectValue = Array.isArray(redirectQuery) ? redirectQuery[0] : redirectQuery;
      const redirectPath =
        typeof redirectValue === "string" && redirectValue.startsWith("/") && !redirectValue.startsWith("//") ? redirectValue : "";
      useLoginDialogStore().open({ redirectPath });
    }
    if (to.path !== FEATURED_PATH) {
      next(FEATURED_PATH);
      return;
    }
  }

  if (isGuest && !isPublicPath) {
    useLoginDialogStore().open({ redirectPath: to.fullPath });
    // `from.path && ...` 的静态类型是 `string | boolean`（空串也跟着走），
    // 直接塞进 next() 会命中重载歧义 ⇒ 显式收成 boolean。
    const stayOnFrom = Boolean(from.path) && isPublicRoutePath(from.path);
    if (stayOnFrom) {
      next(false);
    } else {
      next(FEATURED_PATH);
    }
    return;
  }

  if (to.path !== "/" && to.path !== FEATURED_PATH && to.path !== "/login") {
    localStorage.setItem("last_page", to.fullPath);
  }

  if (isGuest) {
    // 创作 / 关注 / 我的 / 灵感中心：进入访客页，并自动弹出登录框
    if (isGuestAuthPromptPath(to.path)) {
      useLoginDialogStore().open({ redirectPath: to.fullPath });
    } else if (pendingInviteCaptured && !useLoginDialogStore().visible) {
      // 扫码落地（公开页，如精选页）：直接弹出登录框 —— LoginDialog 打开时会把
      // 暂存的邀请码回填进**注册**表单，用户看到的就是「注册页 + 邀请码已填好」。
      useLoginDialogStore().open({});
    }
    next();
    return;
  }

  // 4.0> 动态获取用户信息（已登录用户） ----------------------------------------------------------------
  try {
    await common.getUserInfo(token, signKey as string);
    // 4.1> 幂等初始化梦想豆类型枚举 + 扣费规则（刷新后首次进入即可用）
    await usePowerStore().init();
  } catch {
    common.signOut();
    sessionStorage.setItem("session_expired", "1");
    useLoginDialogStore().open({ redirectPath: to.fullPath, sessionExpired: true });
    next(FEATURED_PATH);
    return;
  }

  // 5.0> 动态获取路由信息 -----------------------------------------------------------------------------
  // const routersData = await common.getRouters(token);

  // 6.0> 直接放行"404" ---------------------------------------------------------------------
  if (to.name == "404") {
    next();
    return;
  }

  // 7.0> 管理员路由校验 ---------------------------------------------------------------------------
  if (to.meta.requiresAdmin) {
    const userInfo = common.getSynUserInfo();
    if (!userInfo || userInfo.role !== "admin") {
      next("/home");
      return;
    }
  }

  if (to.meta.requiresTenantAdmin) {
    // 成员管理：需企业管理员 role=1 且 ENTERPRISE 登录身份
    const userInfo = common.getSynUserInfo();
    if (!userInfo || userInfo.role !== "1" || userInfo.loginMode !== "ENTERPRISE" || !isEnterpriseLevel(userInfo.level)) {
      next("/home");
      return;
    }
  }

  next();
});

router.afterEach((to) => {
  // 登录校验是异步的，结束后再写一次标题，避免被中途的中文 meta 盖住
  applyRouteDocumentTitle(to.path, to.meta.title);
  // 0.0 页面顶部进度条：关闭 ---------------------------------------------------------------------
  nprogressClose();
});

router.onError((err) => {
  if (appUpdate.isChunkLoadError(err)) {
    appUpdate.reloadForUpdate(router.currentRoute.value.fullPath);
  }
});

export default router;
